interface Warehouse {
  name: string;
  phone: string;
  fax: string;
  address: string;
  region: string;
}

interface WarehouseTableProps {
  warehouses: Warehouse[];
}

export default function WarehouseTable({ warehouses }: WarehouseTableProps) {
  // Group warehouses by region
  const groupedWarehouses = warehouses.reduce((acc, warehouse) => {
    if (!acc[warehouse.region]) {
      acc[warehouse.region] = [];
    }
    acc[warehouse.region].push(warehouse);
    return acc;
  }, {} as Record<string, Warehouse[]>);

  const getRegionBgColor = (region: string) => {
    if (region === "경기도 광주") return "bg-[#fbcad3]";
    if (region === "경기도 용인") return "bg-[#bf8eea]";
    if (region === "기타지역") return "bg-[#dffddd]";
    if (region === "부산") return "bg-[#cae2fb]";
    return "bg-[#fbcad3]";
  };

  return (
    <div className="mt-4 sm:mt-8">
      {/* Mobile Card Layout */}
      <div className="block sm:hidden space-y-4">
        {Object.entries(groupedWarehouses).map(([region, regionWarehouses]) => (
          <div key={region} className="space-y-3">
            {/* Region Header */}
            <div className={`${getRegionBgColor(region)} rounded-lg p-3`}>
              <h3 className="text-base font-bold text-black text-center">{region}</h3>
            </div>

            {/* Warehouse Cards */}
            <div className="space-y-3">
              {regionWarehouses.map((warehouse, index) => (
                <div key={index} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                  <div className="space-y-2">
                    <h4 className="text-base font-semibold text-gray-900">{warehouse.name}</h4>
                    <div className="grid grid-cols-1 gap-2 text-sm">
                      <div className="flex">
                        <span className="font-medium text-gray-600 w-12 flex-shrink-0">전화:</span>
                        <a href={`tel:${warehouse.phone}`} className="text-blue-600 hover:text-blue-800">
                          {warehouse.phone}
                        </a>
                      </div>
                      {warehouse.fax && (
                        <div className="flex">
                          <span className="font-medium text-gray-600 w-12 flex-shrink-0">팩스:</span>
                          <span className="text-gray-900">{warehouse.fax}</span>
                        </div>
                      )}
                      <div className="flex">
                        <span className="font-medium text-gray-600 w-12 flex-shrink-0">주소:</span>
                        <span className="text-gray-900 break-words">{warehouse.address}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Desktop Table Layout */}
      <div className="hidden sm:block overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <tbody className="bg-white divide-y divide-gray-200">
            {warehouses.map((warehouse, index) => {
              const isFirstOfRegion = index === 0 || warehouses[index - 1].region !== warehouse.region;
              const tableRowData = [];
              const regionBgColor = getRegionBgColor(warehouse.region);

              if (isFirstOfRegion) {
                tableRowData.push(
                  <tr key="region-header" className={regionBgColor}>
                    <td className="px-3 py-2 text-center text-lg font-bold text-black uppercase tracking-wider" colSpan={4}>
                      {warehouse.region}
                    </td>
                  </tr>
                );
                tableRowData.push(
                  <tr key="data-header" className="bg-gray-50">
                    <th className="px-3 py-2 text-left text-sm font-medium text-black uppercase tracking-wider">
                      창고명
                    </th>
                    <th className="px-3 py-2 text-left text-sm font-medium text-black uppercase tracking-wider">
                      전화
                    </th>
                    <th className="px-3 py-2 text-left text-sm font-medium text-black uppercase tracking-wider">
                      팩스
                    </th>
                    <th className="px-3 py-2 text-left text-sm font-medium text-black uppercase tracking-wider">
                      주소
                    </th>
                  </tr>
                );
              }
              tableRowData.push(
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                    {warehouse.name}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                    <a href={`tel:${warehouse.phone}`} className="text-blue-600 hover:text-blue-800">
                      {warehouse.phone}
                    </a>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                    {warehouse.fax}
                  </td>
                  <td className="px-3 py-2 text-sm text-gray-500">
                    {warehouse.address}
                  </td>
                </tr>
              );
              return tableRowData;
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
} 